import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { AuthContext } from '../contexts/AuthContext';

import { Helmet } from 'react-helmet';
import SectionHeader from './SectionHeader';
import Loader from './Loader';

import GeneralInformation from './UserProfile/GeneralInformation';
import ContactDetails from './UserProfile/ContactDetails';
import PasswordReset from './UserProfile/PasswordReset';
import AdminLicenseTypes from './UserProfile/AdminLicenseTypes';
import AdminDocumentTypes from './UserProfile/AdminDocumentTypes';
import ConnectGoogleAccount from './UserProfile/ConnectGoogleAccount';
import AdminUserList from './UserProfile/AdminUserList';
import AdminNotificationPreferences from './UserProfile/AdminNotificationPreferences';

import authService from '../services/authService';
import userService from '../services/userService';

import { Squares2X2Icon, PhoneIcon, LockClosedIcon, DocumentIcon, LinkIcon, ShieldCheckIcon, BellIcon } from '@heroicons/react/24/outline';
import { ArrowLeftEndOnRectangleIcon } from '@heroicons/react/20/solid';

const AdminProfile = () => {
  const { user, mutateUser, setToken } = useContext(AuthContext);

  const [isLoading, setIsLoading] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const [isLinkingGoogle, setIsLinkingGoogle] = useState(false);

  const steps = [
    {
      id: 'general-information',
      label: 'General information'
    },
    {
      id: 'contact-details',
      label: 'Contact details'
    },
    {
      id: 'password-reset',
      label: 'Password reset'
    },
    {
      id: 'google-connect',
      label: 'Google connect'
    },
    {
      id: 'license-types',
      label: 'License types'
    },
    {
      id: 'document-types',
      label: 'Document Types'
    }, {
      id: 'admins',
      label: 'Admins'
    },
    {
      id: 'notification-preferences',
      label: 'Notifications'
    }
  ];

  const StepIcon = ({ step }) => {
    switch (step) {
      case 'general-information':
        return <Squares2X2Icon className='h-5 w-5 text-ase-blue' />;
      case 'contact-details':
        return <PhoneIcon className='h-5 w-5 text-ase-blue' />;
      case 'password-reset':
        return <LockClosedIcon className='h-5 w-5 text-ase-blue' />;
      case 'google-connect':
        return <LinkIcon className='h-5 w-5 text-ase-blue' />;
      case 'license-types':
        return <DocumentIcon className='h-5 w-5 text-ase-blue' />;
      case 'document-types':
        return <DocumentIcon className='h-5 w-5 text-ase-blue' />;
      case 'admins':
        return <ShieldCheckIcon className='h-5 w-5 text-ase-blue' />;
      case 'notification-preferences':
        return <BellIcon className='h-5 w-5 text-ase-blue' />;
      default:
        return <Squares2X2Icon className='h-5 w-5 text-ase-blue' />;
    }
  }

  const [currentStep, setCurrentStep] = useState(steps.find(step => step.id === 'general-information'));

  const personalSteps = ['general-information', 'contact-details', 'password-reset', 'google-connect'];
  const adminSteps = ['license-types', 'document-types', 'admins', 'notification-preferences'];

  useEffect(() => {
    const linkGoogleAccount = async () => {
      try {
        const code = searchParams.get('code');

        await authService.linkGoogleAccount({
          code,
          userId: user._id,
          redirectUri: `${process.env.REACT_APP_APP_URL}/admin/profile`
        });

        mutateUser();

        toast.success('Google account linked');
      } catch (error) {
        toast.error('Something went wrong');
      } finally {
        setIsLinkingGoogle(false);
        setSearchParams({});
      }
    };

    if (searchParams.has('code') && user?._id) {
      if (!isLinkingGoogle) {
        setIsLinkingGoogle(true);

        linkGoogleAccount();
      }
    }
  }, [searchParams, isLinkingGoogle, mutateUser, user?._id, setSearchParams])

  const handleGeneralInformationSubmit = async (data) => {
    try {
      setIsLoading(true);

      await userService.updateUser({
        userId: user._id,
        updateData: {
          ...data
        }
      });

      mutateUser();

      toast.success('General information updated');
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  }

  const handleContactDetailsSubmit = async (data) => {
    try {
      setIsLoading(true);

      await userService.updateUser({
        userId: user._id,
        updateData: {
          ...data
        }
      });

      mutateUser();

      toast.success('General information updated');
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  }

  const handlePasswordResetSubmit = async (data) => {
    try {
      setIsLoading(true);

      await authService.updatePassword({
        userId: user._id,
        password: data.password
      });

      mutateUser();

      toast.success('Password changed');
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    mutateUser(null);
  }

  if (searchParams.has('code')) {
    return (
      <>
        <Helmet>
          <title>Profile | ASE Group</title>
        </Helmet>

        <div className='min-h-[80vh] flex flex-col gap-4 items-center justify-center'>
          <Loader />
          <p>Linking your Google account</p>
        </div>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>Profile | ASE Group</title>
      </Helmet>

      <div className='flex flex-col md:flex-row justify-between w-full md:w-[1000px] mx-auto mb-10 gap-4 md:gap-8'>
        <div className='flex gap-4 bg-ase-blue md:hidden overflow-scroll p-2 rounded-md'>
          {steps.map(step => (
            <button
              onClick={() => setCurrentStep(step)}
              key={step.id}
              className={`${currentStep.id === step.id ? 'bg-white text-ase-blue' : 'bg-white text-black bg-opacity-65'} px-2 flex items-center gap-2 hover:text-ase-blue py-2 text-xs w-full rounded-md text-nowrap`}
            >
              <StepIcon step={step.id} />
              <span>{step.label}</span>
            </button>
          ))}
        </div>

        <div className='hidden md:flex flex-col gap-4'>
          <div className='border-ase-blue border-x border-t rounded-lg h-fit w-[250px]'>
            <SectionHeader title='Personal' />

            <div className='bg-white p-2 border-b border-ase-blue rounded-b-lg'>
              {steps.filter(s => personalSteps.includes(s.id)).map(step => (
                <button
                  onClick={() => setCurrentStep(step)}
                  key={step.id}
                  className={`${currentStep.id === step.id ? 'bg-ase-blue text-ase-blue bg-opacity-10' : 'text-black'} px-2 flex items-center gap-2 hover:text-ase-blue py-2 text-sm w-full rounded-md`}
                >
                  <StepIcon step={step.id} />
                  <span>{step.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className='border-ase-blue border-x border-t rounded-lg h-fit w-[250px]'>
            <SectionHeader title='Admin' />

            <div className='bg-white p-2 border-b border-ase-blue rounded-b-lg'>
              {steps.filter(s => adminSteps.includes(s.id)).map(step => (
                <button
                  onClick={() => setCurrentStep(step)}
                  key={step.id}
                  className={`${currentStep.id === step.id ? 'bg-ase-blue text-ase-blue bg-opacity-10' : 'text-black'} px-2 flex items-center gap-2 hover:text-ase-blue py-2 text-sm w-full rounded-md`}
                >
                  <StepIcon step={step.id} />
                  <span>{step.label}</span>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleLogout}
            className='bg-red-600 text-white font-semibold h-[36px] rounded-md w-[250px] flex items-center justify-center gap-1 text-sm'
          >
            <ArrowLeftEndOnRectangleIcon className='h-5 w-5' /> Sign out
          </button>
        </div>

        <div className='border border-ase-blue rounded-lg h-fit flex-grow bg-white pb-1'>
          <SectionHeader title={currentStep.label} />

          {!adminSteps.includes(currentStep.id) && (
            <div className='p-4'>
              {currentStep.id === 'general-information' && <GeneralInformation
                includeTitle={false}
                isLoading={isLoading}
                onSubmit={handleGeneralInformationSubmit}
              />}

              {currentStep.id === 'password-reset' && <PasswordReset
                isLoading={isLoading}
                onSubmit={handlePasswordResetSubmit}
              />}

              {currentStep.id === 'contact-details' && <ContactDetails
                isLoading={isLoading}
                onSubmit={handleContactDetailsSubmit}
              />}

              {currentStep.id === 'google-connect' && <ConnectGoogleAccount
                isLoading={isLoading}
                onSubmit={handleContactDetailsSubmit}
              />}
            </div>
          )}

          {currentStep.id === 'license-types' && <AdminLicenseTypes />}
          {currentStep.id === 'document-types' && <AdminDocumentTypes />}
          {currentStep.id === 'notification-preferences' && <AdminNotificationPreferences />}

          {user?.permissions?.includes('update-permissions') && currentStep.id === 'admins' && (
            <>
              {currentStep.id === 'admins' && <AdminUserList />}
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default AdminProfile;
